import React from "react";
import Image from "next/image";
import { GetStaticProps, GetStaticPaths } from "next";
// components
import CustomHead from "src/components/ui/CustomHead";
import Layout from "src/layout";
import Link from "src/components/ui/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Users, TrendingUp } from "lucide-react";
import useAllPage from "src/hooks/dynamic/allPage";
// constants
import { EVariants } from "src/constants/variants";
// data
import { getCaseStudyBySlug, getAllCaseStudySlugs, CaseStudy } from "data/case-studies";

interface CaseStudyPageProps {
  caseStudy: CaseStudy;
  initialReduxState: any;
}

const getMetricIcon = (icon?: string) => {
  switch (icon) {
    case "clock":
      return <Clock className="h-8 w-8 text-white" />;
    case "users":
      return <Users className="h-8 w-8 text-white" />;
    case "trending":
      return <TrendingUp className="h-8 w-8 text-white" />;
    default:
      return null;
  }
};

export default function CaseStudyPage({ caseStudy, initialReduxState }: CaseStudyPageProps) {
  if (!caseStudy) {
    return (
      <>
        <CustomHead title="Case Study Not Found | PetsApp" description="Case study not found." />
        <Layout headerProps={{ variant: EVariants.PRIMARY }}>
          <div className="min-h-screen bg-primary flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl font-cooper font-bold mb-4">Case Study Not Found</h1>
              <p className="text-white/80 font-brown mb-8">The case study you're looking for doesn't exist.</p>
              <Link href="/case-studies">
                <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full">
                  Back to Case Studies
                </Button>
              </Link>
            </div>
          </div>
        </Layout>
      </>
    );
  }

  const hasDetailedContent =
    caseStudy.content?.challenge || caseStudy.content?.solution || caseStudy.content?.results;
  const hasCustomSections = caseStudy.content?.customSections && caseStudy.content.customSections.length > 0;

  return (
    <>
      <CustomHead
        title={`${caseStudy.title} | PetsApp Case Studies`}
        description={caseStudy.description}
      />
      <Layout headerProps={{ variant: EVariants.PRIMARY }}>
        <div className="min-h-screen">
          {/* Hero Section */}
          <section className="bg-primary py-16 md:py-24 relative overflow-hidden">
            <div className="container mx-auto px-4 md:px-6">
              <div
                className={`grid ${hasDetailedContent || hasCustomSections ? "lg:grid-cols-2" : "md:grid-cols-2"} gap-12 items-center`}
              >
                <div className="space-y-6">
                  {(hasDetailedContent || hasCustomSections) && (
                    <Badge className="bg-secondary text-tertiary hover:bg-secondary/80">Case Study</Badge>
                  )}
                  <h1 className="text-3xl font-bold sm:text-4xl md:text-5xl lg:text-6xl text-white font-cooper">
                    {caseStudy.title}
                  </h1>
                  <p className="text-xl text-white font-brown">{caseStudy.description}</p>
                  {(hasDetailedContent || hasCustomSections) && (
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link href="https://meetings.hubspot.com/naomi-oikonomou/revenue-growth">
                        <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full shadow-[0_4px_14px_0_rgba(255,255,255,0.5)] relative overflow-hidden after:content-[''] after:absolute after:h-1/2 after:w-full after:left-0 after:bottom-0 after:bg-gradient-to-t after:from-white/30 after:to-transparent after:rounded-full after:animate-pulse">
                          Schedule Your Demo
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <div className="relative rounded-xl overflow-hidden">
                    <Image
                      src={caseStudy.image}
                      alt={caseStudy.title}
                      width={600}
                      height={400}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Metrics Section */}
          {caseStudy.metrics && caseStudy.metrics.length > 0 && (
            <section className={`bg-primary ${hasDetailedContent || hasCustomSections ? "py-16" : "py-8 md:py-16"}`}>
              <div className="container mx-auto px-4 md:px-6">
                <div
                  className={`grid grid-cols-1 ${caseStudy.metrics.length === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"} gap-8 text-center text-white`}
                >
                  {caseStudy.metrics.map((metric, index) => (
                    <div
                      key={index}
                      className={hasDetailedContent || hasCustomSections ? "flex flex-col items-center" : ""}
                    >
                      {(hasDetailedContent || hasCustomSections) && metric.icon && (
                        <div className="bg-secondary/20 p-4 rounded-full mb-4">
                          {getMetricIcon(metric.icon)}
                        </div>
                      )}
                      <h4 className="text-4xl md:text-5xl font-bold font-cooper mb-2">{metric.value}</h4>
                      {metric.description.includes(" - ") ? (
                        <>
                          <p className="text-lg font-semibold text-white mb-2 font-cooper">
                            {metric.description.split(" - ")[0]}
                          </p>
                          <p className="text-white font-brown">{metric.description.split(" - ")[1]}</p>
                        </>
                      ) : (
                        <p className="text-white/80 font-brown">{metric.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Content Section */}
          {caseStudy.content && (
            <section className="bg-primary py-16 relative overflow-hidden">
              <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="bg-tertiary rounded-xl p-8 md:p-12 text-white max-w-4xl mx-auto relative overflow-hidden">
                  {/* Background decorative elements for detailed content */}
                  {(hasDetailedContent || hasCustomSections) && (
                    <>
                      <div className="absolute right-0 bottom-0 opacity-10 translate-x-1/4 translate-y-1/4">
                        <Image
                          src="https://petsapp.com/_next/image?url=%2Fimages%2FHome%2Fyorkie.png&w=3840&q=75"
                          alt="Yorkie illustration"
                          width={300}
                          height={300}
                          className="w-[300px] h-[300px]"
                        />
                      </div>
                      <div className="absolute left-0 top-0 opacity-5 -translate-x-1/4 -translate-y-1/4">
                        <Image
                          src="https://petsapp.com/_next/image?url=%2Fimages%2FHome%2Fstethoscope.png&w=3840&q=75"
                          alt="Stethoscope illustration"
                          width={200}
                          height={200}
                          className="w-[200px] h-[200px]"
                        />
                      </div>
                    </>
                  )}

                  <div className={`relative z-10 ${hasDetailedContent || hasCustomSections ? "space-y-6" : "space-y-8"}`}>
                    {/* Simple content layout */}
                    {caseStudy.content.body && !hasDetailedContent && !hasCustomSections && (
                      <p className="text-white/90 font-brown text-lg leading-relaxed">{caseStudy.content.body}</p>
                    )}

                    {/* Custom sections (for special case studies like PetsApp Together) */}
                    {hasCustomSections && (
                      <>
                        {caseStudy.content.body && (
                          <p className="text-lg text-white font-brown leading-relaxed">{caseStudy.content.body}</p>
                        )}
                        {caseStudy.content.customSections?.map((section, index) => (
                          <div key={index} className="space-y-6">
                            {section.title && (
                              <h2 className="text-2xl md:text-3xl font-bold text-white font-cooper">{section.title}</h2>
                            )}
                            {section.content && (
                              <p className="text-white font-brown leading-relaxed text-lg mb-4">
                                {section.content.split(/(\{link:\d+\})/).map((part, i) => {
                                  const linkMatch = part.match(/\{link:(\d+)\}/);
                                  if (linkMatch && section.links) {
                                    const linkIndex = parseInt(linkMatch[1]);
                                    const link = section.links[linkIndex];
                                    if (link) {
                                      return (
                                        <Link
                                          key={i}
                                          href={link.url}
                                          anchorProps={{
                                            target: "_blank",
                                            rel: "noopener noreferrer",
                                            className: "text-white hover:text-white/80 underline font-semibold",
                                          }}
                                        >
                                          {link.text}
                                        </Link>
                                      );
                                    }
                                  }
                                  return <React.Fragment key={i}>{part}</React.Fragment>;
                                })}
                              </p>
                            )}
                            {section.list && (
                              <ul className="text-white space-y-3 ml-6 mb-4 font-brown">
                                {section.list.map((item, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <span className="text-accent mr-2">•</span>
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {section.title === "Essential Features for Mobile Success" &&
                              caseStudy.content?.features &&
                              caseStudy.content.features.length > 0 && (
                                <div className="grid md:grid-cols-2 gap-6">
                                  {caseStudy.content.features.map((feature, idx) => (
                                    <div key={idx} className="bg-white/10 rounded-lg p-4">
                                      <h4 className="text-lg font-semibold text-white mb-2 font-cooper">
                                        {feature.title}
                                      </h4>
                                      <p className="text-white text-sm font-brown">{feature.description}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            {section.button && (
                              <div className="pt-8">
                                <Link
                                  href={section.button.url}
                                  anchorProps={{
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                  }}
                                >
                                  <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full px-8 py-4 text-lg font-brown shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                                    {section.button.text}
                                  </Button>
                                </Link>
                              </div>
                            )}
                          </div>
                        ))}
                        {hasCustomSections && caseStudy.content.quote && (
                          <div className="bg-white/10 rounded-2xl p-6 mt-8">
                            <blockquote className="text-white italic text-lg mb-4 font-brown">
                              "{caseStudy.content.quote.text}"
                            </blockquote>
                            <cite className="text-white/80 text-sm font-brown">
                              — {caseStudy.content.quote.author}
                            </cite>
                          </div>
                        )}
                        {caseStudy.content.footerText && (
                          <div className="pt-8 border-t border-white/20">
                            <p className="text-white font-brown text-sm">{caseStudy.content.footerText}</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Detailed content layout */}
                    {hasDetailedContent && !caseStudy.content?.customSections && (
                      <>
                        {caseStudy.content.about && (
                          <>
                            <h2 className="text-3xl md:text-4xl font-cooper font-bold mb-6">About {caseStudy.title.split(":")[0]}</h2>
                            <p className="text-lg text-white/90 font-brown leading-relaxed mb-6">
                              {caseStudy.content.about}
                            </p>
                          </>
                        )}
                        {caseStudy.content.challenge && (
                          <>
                            <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-6">The Challenge</h2>
                            <p className="text-white font-brown text-lg leading-relaxed">
                              {caseStudy.content.challenge}
                            </p>
                            {caseStudy.content.challengeList && (
                              <ul className="list-disc list-inside space-y-2 text-white font-brown text-lg ml-4">
                                {caseStudy.content.challengeList.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}

                        {caseStudy.content.solution && (
                          <>
                            <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-6 mt-8">
                              The PetsApp Solution
                            </h2>
                            <p className="text-white font-brown text-lg leading-relaxed">
                              {caseStudy.content.solution}
                            </p>
                          </>
                        )}

                        {caseStudy.content.results && (
                          <>
                            <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-6 mt-8">
                              {caseStudy.content.results.includes("Remarkable") ? "The Remarkable Results" : "The Results"}
                            </h2>
                            <p className="text-white font-brown text-lg leading-relaxed">
                              {caseStudy.content.results}
                            </p>
                            {caseStudy.content.resultsList && (
                              <ul className="list-disc list-inside space-y-2 text-white font-brown text-lg ml-4">
                                {caseStudy.content.resultsList.map((item, index) => {
                                  // Handle results that have both bold and additional text
                                  const parts = item.text.split(item.bold || "");
                                  return (
                                    <li key={index}>
                                      {item.bold ? (
                                        <>
                                          <strong>{item.bold}</strong>
                                          {parts[1] || ""}
                                        </>
                                      ) : (
                                        item.text
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </>
                        )}

                        {caseStudy.content.quote && (
                          <blockquote className="border-l-4 border-accent pl-6 my-8">
                            <p className="text-white font-brown text-lg italic leading-relaxed">
                              "{caseStudy.content.quote.text}"
                            </p>
                            <footer className="text-white font-brown mt-4">— {caseStudy.content.quote.author}</footer>
                          </blockquote>
                        )}

                        {caseStudy.content.impact && (
                          <>
                            <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-6 mt-8">
                              The Impact on Practice Growth
                            </h2>
                            <p className="text-white font-brown text-lg leading-relaxed">
                              {caseStudy.content.impact}
                            </p>
                            {caseStudy.content.impactList && (
                              <ul className="list-disc list-inside space-y-2 text-white font-brown text-lg ml-4">
                                {caseStudy.content.impactList.map((item, index) => (
                                  <li key={index}>{item}</li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}

                        {caseStudy.content.features && caseStudy.content.features.length > 0 && (
                          <>
                            <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-6 mt-8">
                              {caseStudy.content.features[0]?.title === "Smart Route Optimization"
                                ? "Essential Features for Mobile Success"
                                : caseStudy.content.features[0]?.title === "Smart Call Management"
                                  ? "Key Features That Solved the Communication Crisis"
                                  : caseStudy.content.features[0]?.title === "Digital Appointment Booking"
                                    ? "Key Success Features"
                                    : "Key Features That Made the Difference"}
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                              {caseStudy.content.features.map((feature, index) => (
                                <div key={index}>
                                  <h3 className="text-xl font-cooper font-bold mb-3 text-accent">
                                    {feature.title}
                                  </h3>
                                  <p className="text-white font-brown">{feature.description}</p>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* CTA Section for detailed case studies */}
          {hasDetailedContent && !caseStudy.content?.customSections && (
            <section className="py-16 bg-gray-50">
              <div className="container mx-auto px-4 md:px-6 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 font-cooper">
                  Ready to Transform Your Customer Service Experience?
                </h2>
                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto font-brown">
                  Join {caseStudy.title.split(":")[0]} and hundreds of other practices that have revolutionized their client
                  relationships with PetsApp. Discover how our comprehensive platform can help you achieve similar remarkable
                  results.
                </p>
                <Link href="https://meetings.hubspot.com/naomi-oikonomou/revenue-growth">
                  <Button className="bg-primary hover:bg-primary/90 text-white text-lg px-8 py-4 rounded-full font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-brown">
                    Schedule Your Demo Today
                  </Button>
                </Link>
              </div>
            </section>
          )}

          {/* CTA Section for simple case studies */}
          {!hasDetailedContent && !hasCustomSections && (
            <section className="py-16 bg-primary">
              <div className="container mx-auto px-4 md:px-6">
                <div className="bg-tertiary rounded-xl p-8 md:p-12 text-white text-center max-w-4xl mx-auto relative overflow-hidden">
                  <div className="absolute right-0 bottom-0 opacity-10 translate-x-1/4 translate-y-1/4">
                    <Image
                      src="https://petsapp.com/_next/image?url=%2Fimages%2FHome%2Fyorkie.png&w=3840&q=75"
                      alt="Yorkie illustration"
                      width={300}
                      height={300}
                      className="w-[300px] h-[300px]"
                    />
                  </div>

                  <div className="relative z-10">
                    <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-4">
                      Ready to transform your veterinary practice?
                    </h2>
                    <p className="text-white/90 mb-6 max-w-2xl mx-auto font-brown">
                      Join the hundreds of veterinary practices using PetsApp to transform their client communication and
                      business operations.
                    </p>
                    <div className="flex justify-center">
                      <Link href="https://meetings.hubspot.com/naomi-oikonomou/revenue-growth">
                        <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full shadow-[0_4px_14px_0_rgba(255,255,255,0.5)] relative overflow-hidden after:content-[''] after:absolute after:h-1/2 after:w-full after:left-0 after:bottom-0 after:bg-gradient-to-t after:from-white/30 after:to-transparent after:rounded-full after:animate-pulse">
                          Schedule a demo
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </Layout>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = getAllCaseStudySlugs();
  const paths = slugs.map((slug) => ({
    params: { slug },
  }));

  return {
    paths,
    fallback: false, // 404 for unknown slugs
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const slug = ctx.params?.slug as string;
  const caseStudy = getCaseStudyBySlug(slug);
  const initialReduxState = await useAllPage(ctx.locale as string);

  if (!caseStudy) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      caseStudy,
      initialReduxState: { DYNAMIC: initialReduxState.DYNAMIC },
    },
    revalidate: 600, // 10 Minutes Cache
  };
};

